import { apiUrl, TENANT_ID, BUSINESS_UNIT_ID } from "@/config/api";

const LOYALTY_KEYS = [
  "loyaltyPoints",
  "loyalty_points",
  "loyaltyPoint",
  "loyalty_point",
  "points",
  "totalPoints",
  "total_points",
  "availablePoints",
  "available_points",
  "wallet_balance",
  "walletBalance",
  "rewardPoints",
  "reward_points",
  "currentPoints",
  "current_points",
] as const;

function coercePoints(v: unknown): number | null {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && v.trim()) {
    const n = parseFloat(v.replace(/,/g, ""));
    if (Number.isFinite(n)) return n;
  }
  return null;
}

/** Best-effort loyalty balance from CS `/home` JSON (shape varies by env). */
export function extractLoyaltyPointsFromHomeJson(
  data: unknown,
  seen = new Set<unknown>(),
  depth = 0
): number | null {
  if (depth > 10 || data == null) return null;
  if (typeof data !== "object") return null;
  if (seen.has(data)) return null;
  seen.add(data);

  const o = data as Record<string, unknown>;
  for (const k of LOYALTY_KEYS) {
    if (k in o) {
      const n = coercePoints(o[k]);
      if (n != null) return n;
    }
  }

  for (const v of Object.values(o)) {
    if (v != null && typeof v === "object") {
      const n = extractLoyaltyPointsFromHomeJson(v, seen, depth + 1);
      if (n != null) return n;
    }
  }
  return null;
}

export async function fetchCustomerHome(
  customerId: string,
  limit = 5
): Promise<unknown | null> {
  const id = customerId.trim();
  if (!id) return null;

  const q = new URLSearchParams({
    limit: String(limit),
    customer_id: id,
  });

  const url = `${apiUrl("/home")}?${q}`;
  const res = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      "x-tenant-id": TENANT_ID,
      "x-business-unit-id": BUSINESS_UNIT_ID,
    },
    cache: "no-store",
  });

  if (!res.ok) return null;
  return res.json().catch(() => null);
}

/** Client / shared: `/home` + extract loyalty (same as `/api/customer/home`). */
export async function fetchCustomerHomeLoyaltyPoints(
  customerId: string,
  limit = 5
): Promise<number | null> {
  const id = customerId.trim();
  if (!id) return null;
  const data = await fetchCustomerHome(id, limit);
  if (data == null) return null;
  return extractLoyaltyPointsFromHomeJson(data);
}
